import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { createPortalAccount, fetchPortalAccounts, fetchPortalOptions, fetchUsers } from '../api';
import AccountEditor from './AccountEditor';
import './portaladmin.css';

// Commercial portal setup: which businesses can sign in on the website, what each can
// do, their job form, and their people. Admins only (the API enforces it too).

function AddBusiness({ existing, onAdded, onCancel }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (query.trim().length < 2) { setResults([]); return undefined; }
    const timer = setTimeout(() => {
      fetchUsers(query.trim())
        .then((data) => setResults(data.results || []))
        .catch(() => setError('Could not search customers.'));
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  const add = async (customer) => {
    setBusy(true);
    setError('');
    try {
      onAdded(await createPortalAccount(customer.id));
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not add that business.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pa-add">
      <input
        className="pa-input"
        autoFocus
        placeholder="Search customers"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
      />
      {error && <div className="pa-error">{error}</div>}
      <ul className="pa-add-results">
        {results.map((customer) => {
          const has = existing.has(customer.id);
          return (
            <li key={customer.id}>
              <button type="button" disabled={has || busy} onClick={() => add(customer)}>
                <span>{customer.name}</span>
                <span className="pa-muted">{has ? 'Already has the portal' : [customer.phone, customer.email].filter(Boolean).join(' · ')}</span>
              </button>
            </li>
          );
        })}
      </ul>
      <button type="button" className="pa-link" onClick={onCancel}>Cancel</button>
    </div>
  );
}

function PortalAdmin() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = Number(searchParams.get('account')) || null;
  const [options, setOptions] = useState(null);
  const [accounts, setAccounts] = useState(null);
  const [filter, setFilter] = useState('');
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    fetchPortalAccounts()
      .then((data) => { setAccounts(data); setError(''); })
      .catch(() => setError('Could not load portal accounts.'));
  }, []);

  useEffect(() => {
    load();
    fetchPortalOptions().then(setOptions).catch(() => setError('Could not load portal settings.'));
  }, [load]);

  const select = (id) => setSearchParams(id ? { account: String(id) } : {});

  const onAdded = (account) => {
    setAdding(false);
    load();
    select(account.id);
  };

  const shown = (accounts || []).filter((a) => a.customer_name.toLowerCase().includes(filter.trim().toLowerCase()));

  return (
    <div className="pa-page">
      <div className="pa-intro">
        <h2>Commercial Portal</h2>
        <p>
          Businesses here can sign in on the website (paragonwheelrepair.com/portal) with an emailed code to send in
          jobs and follow them. Nothing on the website links to it yet.
        </p>
      </div>
      {error && <div className="pa-error">{error}</div>}
      <div className="pa-layout">
        <aside className="pa-list">
          <div className="pa-list-toolbar">
            <input className="pa-input" placeholder="Filter" value={filter} onChange={(event) => setFilter(event.target.value)} />
            {!adding && <button type="button" className="pa-button" onClick={() => setAdding(true)}>Add business</button>}
          </div>
          {adding && (
            <AddBusiness
              existing={new Set((accounts || []).map((a) => a.customer_id))}
              onAdded={onAdded}
              onCancel={() => setAdding(false)}
            />
          )}
          {accounts === null && <div className="pa-muted">Loading…</div>}
          {accounts && accounts.length === 0 && !adding && (
            <div className="pa-empty">No businesses have the portal yet. Add one to start.</div>
          )}
          <ul className="pa-accounts">
            {shown.map((account) => (
              <li key={account.id}>
                <button
                  type="button"
                  className={`pa-account ${account.id === selectedId ? 'active' : ''}`}
                  onClick={() => select(account.id)}
                >
                  <span className="pa-account-name">{account.customer_name}</span>
                  <span className="pa-account-meta">
                    {!account.is_enabled && <span className="pa-tag pa-tag-off">Off</span>}
                    {account.people} {account.people === 1 ? 'person' : 'people'}
                    {' · '}
                    {account.default_intake === 'pickup' ? 'Pickup' : 'Drop-off'}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </aside>
        <section className="pa-detail">
          {selectedId && options ? (
            <AccountEditor key={selectedId} accountId={selectedId} options={options} onChanged={load} />
          ) : (
            <div className="pa-placeholder">Choose a business, or add one.</div>
          )}
        </section>
      </div>
    </div>
  );
}

export default PortalAdmin;
